import {
  DataGrid, GridColDef, GridRenderEditCellParams, GridRowModel, GridToolbarProps, ToolbarPropsOverrides, useGridApiRef,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { useState } from 'react';
import startCase from 'lodash/startCase';
import Card from '@mui/material/Card';
import { useSnackbar } from 'notistack';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { BASE_COLORS } from '@mgm/common/utils';
import {
  CustomEditAvatar,
  CustomEditDatePicker,
  CustomEditSingleSelect,
  CustomEditTimePicker, TBulkTableHeader,
} from '../../../../../components/bulk';
import { useEditScheduleShift } from '../../hooks';
import { useEditTable } from '../../../../../hooks/useTable';
import { INITIAL_SCHEDULE_SHIFT } from '../../../../../data';
import { BulkShiftsTableHeader } from './BulkShiftsTableHeader';
import { useUserLocations } from '../../../../../hooks/useCommon';
import { TLocation, TMember } from '../../../../../types';

const BulkShiftsTable = () => {
  const { pageHeight, resizableDiv } = useScreenResize(70);
  const { emptyBulkShifts } = useEditScheduleShift();
  const { enqueueSnackbar } = useSnackbar();

  const {
    rows,
    setRows,
    setRowModesModel,
  } = useEditTable(emptyBulkShifts as Array<{ id: number | string, isNew: boolean }>);
  const { t } = useTranslation(['inputs', 'common', 'timesheets', 'schedule']);
  const { userLocations } = useUserLocations();
  const apiRef = useGridApiRef();

  const [isInformEmployees, setIsInformEmployees] = useState(true);

  const headers: Array<GridColDef> = [
    {
      field: 'employee',
      headerName: t('member', { ns: 'common' }),
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
          <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      editable: true,
      valueFormatter: (params: TMember) => {
        if (params?.email) return startCase(params.name);
        return '';
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value === null || params.props.value === undefined || params.props.value === '';

        return { ...params.props, error: hasError };
      },
      renderEditCell: (params: GridRenderEditCellParams) => <CustomEditAvatar {...params} />,
    },
    {
      field: 'date',
      headerName: t('date', { ns: 'timesheets' }),
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
          <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      editable: true,
      valueFormatter: (params) => {
        if (params) {
          return moment(params).format('MMMM Do YYYY');
        }
        return '';
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value === null || params.props.value === undefined || params.props.value === '';

        return { ...params.props, error: hasError };
      },
      renderEditCell: (params: GridRenderEditCellParams) => <CustomEditDatePicker {...params} />,
    },
    {
      field: 'location',
      headerName: t('locations'),
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
          <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      valueFormatter: (params: TLocation) => {
        if (params.id) return params.name;
        return '';
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value?.length === 0;

        return { ...params.props, error: hasError };
      },
      renderEditCell: (params: GridRenderEditCellParams) => <CustomEditSingleSelect {...params} options={userLocations} />,
      editable: true,
    },
    {
      field: 'startTime',
      headerName: t('shiftStart', { ns: 'timesheets' }),
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
          <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        let hasError = false;
        let errorMessage = '';
        const { otherFieldsProps } = params;

        if (otherFieldsProps?.endTime.value && moment(params.props.value).isAfter(moment(otherFieldsProps?.endTime.value))) {
          hasError = true;
          errorMessage = t('shiftStartTimeCannot', { ns: 'schedule' });
        }

        if (hasError) {
          enqueueSnackbar(errorMessage, {
            variant: 'warning',
            autoHideDuration: 5000,
          });
        }

        return { ...params.props, error: hasError };
      },
      valueFormatter: (params) => {
        if (params) {
          return moment(params).format('hh:mm A');
        }
        return '';
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <CustomEditTimePicker
          placeholder={t('startTime', { ns: 'timesheets' })}
          name="startTime"
          {...params}
        />
      ),
    },
    {
      field: 'endTime',
      headerName: t('shiftEnd', { ns: 'timesheets' }),
      valueFormatter: (params) => {
        if (params) {
          return moment(params).format('hh:mm A');
        }
        return '';
      },
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
          <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value === null || params.props.value === undefined || params.props.value === '';

        return { ...params.props, error: hasError };
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <CustomEditTimePicker
          {...params}
          name="endTime"
          placeholder={t('endTime', { ns: 'timesheets' })}
          minTime={moment(params.row.startTime).add('30', 'minutes')}
        />
      ),
    },
    {
      field: 'breakStart',
      headerName: t('breakStart', { ns: 'timesheets' }),
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const { otherFieldsProps } = params;
        let hasError = false;

        if (otherFieldsProps?.breakStart?.value && !params.props.value) {
          hasError = true;
        } else if (params.props.value && otherFieldsProps?.breakEnd?.value && moment(otherFieldsProps?.breakEnd?.value)
          .isSameOrBefore(moment(params.props.value))) {
          hasError = true;
        }

        return { ...params.props, error: hasError };
      },
      valueFormatter: (params) => {
        if (params) {
          return moment(params).format('hh:mm A');
        }
        return '';
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <CustomEditTimePicker
          minTime={moment(params.row.startTime)}
          placeholder={t('startTime', { ns: 'timesheets' })}
          name="breakStart"
          {...params}
        />
      ),
    },
    {
      field: 'breakEnd',
      headerName: t('breakEnd', { ns: 'timesheets' }),
      valueFormatter: (params) => {
        if (params) {
          return moment(params).format('hh:mm A');
        }
        return '';
      },
      renderHeader: (params) => (
        <Box alignItems="center" display="flex">
          <Typography variant="caption">{params.colDef.headerName}</Typography>
        </Box>
      ),
      minWidth: 130,
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const { otherFieldsProps } = params;
        let hasError = false;
        let errorMessage = '';

        if (
          (otherFieldsProps?.breakStart?.value && !params.props.value)
        ) {
          hasError = true;
          errorMessage = t('breakEndTimeIsRequired', { ns: 'schedule' });
        } else if (
          moment(params.props.value).isSameOrBefore(moment(otherFieldsProps?.breakStart?.value))
        ) {
          hasError = true;
          errorMessage = t('breakEndTimeMust', { ns: 'schedule' });
        }

        if (hasError) {
          enqueueSnackbar(errorMessage, {
            variant: 'warning',
            autoHideDuration: 5000,
          });
        }

        return { ...params.props, error: hasError };
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <CustomEditTimePicker
          {...params}
          name="breakEnd"
          placeholder={t('endTime', { ns: 'timesheets' })}
          maxTime={moment(params.row.endTime)}
        />
      ),
    },
  ];

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false } as typeof INITIAL_SCHEDULE_SHIFT;

    // if (!isMemberValid(newRow, rows)) return newRow;

    const updatedRows = rows.map((row) => (row?.id === newRow?.id ? updatedRow : row));
    setRows(updatedRows as Array<{ id: number | string, isNew: boolean }>);

    return updatedRow;
  };

  const renderBulkHeader = (props: TBulkTableHeader) => (
    <BulkShiftsTableHeader
      {...props}
      isInformEmployees={isInformEmployees}
      setIsInformEmployees={setIsInformEmployees}
    />
  );

  return (
    <div ref={resizableDiv}>
      <Card
        className="overflow-auto"
        sx={{
          padding: 0,
          height: pageHeight !== 0 ? pageHeight : 'auto',
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={headers}
          rowHeight={50}
          editMode="row"
          hideFooter
          processRowUpdate={processRowUpdate}
          slots={{
            toolbar: renderBulkHeader as any,
          }}
          slotProps={{
            toolbar: {
              setRows, setRowModesModel, rows,
            } as Partial<GridToolbarProps & ToolbarPropsOverrides> | undefined,
          }}
          showCellVerticalBorder
          showColumnVerticalBorder
        />
      </Card>
    </div>
  );
};

export {
  BulkShiftsTable,
};
